import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'how-to-complain', title: 'How to Make a Complaint' },
  { id: 'what-to-include', title: 'What to Include' },
  { id: 'categories', title: 'Complaint Categories' },
  { id: 'process', title: 'Our Process' },
  { id: 'timelines', title: 'Response Timelines' },
  { id: 'escalation', title: 'Escalation' },
  { id: 'external-bodies', title: 'External Bodies' },
  { id: 'confidentiality', title: 'Confidentiality' },
  { id: 'feedback', title: 'Using Feedback to Improve' },
  { id: 'contact', title: 'Contact Us' },
];

export const ComplaintsProcedure = () => {
  return (
    <PolicyLayout title="Complaints Procedure" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="introduction">
        <h2>Introduction</h2>
        <ul>
          <li>
            NursingCareBooking.com is committed to providing a high-quality service. We take all
            complaints seriously and use them as an opportunity to improve.
          </li>
          <li>
            This procedure outlines how to raise a complaint, what to expect during the process, and
            how to escalate if you are not satisfied with the outcome.
          </li>
        </ul>
        <p>This procedure applies to complaints about:</p>
        <ul>
          <li>The Platform and its features</li>
          <li>Care Providers listed on the Platform</li>
          <li>The conduct of Service Users</li>
          <li>Our customer support</li>
          <li>Data protection and privacy concerns</li>
        </ul>
      </section>

      <section id="how-to-complain">
        <h2>How to Make a Complaint</h2>
        <p>You can submit a complaint through any of the following channels:</p>
        <ContactBlock
          variant="full"
          showHours
          extraContent={
            <>
              <p>You can also reach us through:</p>
              <ul>
                <li>
                  <strong>In-app:</strong> Use the messaging feature to contact our support team
                </li>
                <li>
                  <strong>Post:</strong> NursingCareBooking.com, Customer Complaints (provide postal
                  address if applicable)
                </li>
              </ul>
              <p>
                Please include as much detail as possible to help us investigate and resolve your
                complaint efficiently.
              </p>
            </>
          }
        />
      </section>

      <section id="what-to-include">
        <h2>What to Include</h2>
        <p>When submitting a complaint, please provide:</p>
        <ul>
          <li>Your full name and account email address</li>
          <li>The date(s) the issue occurred</li>
          <li>A clear description of the complaint</li>
          <li>Names of any individuals involved (if applicable)</li>
          <li>Any booking reference numbers related to the complaint</li>
          <li>
            Evidence or documentation supporting your complaint (screenshots, messages, photos)
          </li>
          <li>The outcome you are seeking</li>
          <li>Any steps you have already taken to resolve the issue</li>
        </ul>
      </section>

      <section id="categories">
        <h2>Complaint Categories</h2>
        <ul>
          <li>
            <strong>Care Quality:</strong> Concerns about the standard of care provided by a Care
            Provider
          </li>
          <li>
            <strong>Provider Conduct:</strong> Unprofessional behaviour, failure to attend bookings,
            misrepresentation of qualifications
          </li>
          <li>
            <strong>Service User Conduct:</strong> Inappropriate behaviour, harassment, or unsafe
            conditions
          </li>
          <li>
            <strong>Platform Issues:</strong> Technical problems, billing errors, account access
            issues
          </li>
          <li>
            <strong>Privacy and Data:</strong> Concerns about how your personal data is handled
          </li>
          <li>
            <strong>Safeguarding:</strong> Concerns about abuse, neglect, or the safety of a
            vulnerable person (these are treated as urgent and may be escalated immediately -- see
            our Safeguarding Policy)
          </li>
          <li>
            <strong>Discrimination:</strong> Any form of unfair treatment based on protected
            characteristics
          </li>
        </ul>
      </section>

      <section id="process">
        <h2>Our Process</h2>
        <ol>
          <li>
            <strong>Step 1 -- Acknowledgement:</strong> We acknowledge receipt of your complaint
            within 2 working days.
          </li>
          <li>
            <strong>Step 2 -- Assessment:</strong> We assess the complaint and assign it to the
            appropriate team member. You may be contacted for additional information.
          </li>
          <li>
            <strong>Step 3 -- Investigation:</strong> We investigate the complaint thoroughly. This
            may involve reviewing booking records, messages, reviews, and contacting the other party
            involved.
          </li>
          <li>
            <strong>Step 4 -- Resolution:</strong> We aim to resolve the complaint and communicate
            the outcome to you. Possible outcomes include:
            <ul>
              <li>An apology and explanation</li>
              <li>Corrective action (e.g., provider warning, account suspension)</li>
              <li>Mediation between parties</li>
              <li>Policy or process changes</li>
              <li>Referral to an external body where appropriate</li>
            </ul>
          </li>
          <li>
            <strong>Step 5 -- Follow-up:</strong> We follow up to ensure you are satisfied with the
            resolution and to identify any further actions needed.
          </li>
        </ol>
      </section>

      <section id="timelines">
        <h2>Response Timelines</h2>
        <ul>
          <li>
            <strong>Acknowledgement:</strong> Within 2 working days of receiving your complaint
          </li>
          <li>
            <strong>Initial assessment:</strong> Within 5 working days
          </li>
          <li>
            <strong>Investigation and resolution:</strong> We aim to resolve most complaints within
            14 working days
          </li>
          <li>
            <strong>Complex complaints:</strong> Where additional investigation is required, we will
            keep you informed of progress and aim to resolve within 28 working days
          </li>
        </ul>
        <p>
          If we are unable to meet these timelines, we will notify you and explain the reason for
          the delay.
        </p>
      </section>

      <section id="escalation">
        <h2>Escalation</h2>
        <p>If you are not satisfied with the outcome of your complaint:</p>
        <ul>
          <li>
            <strong>Stage 1 -- Internal review:</strong> Request a review of the decision by a
            senior team member. This should be requested within 14 days of receiving the outcome.
          </li>
          <li>
            <strong>Stage 2 -- External escalation:</strong> If the internal review does not resolve
            the matter, you may escalate to the relevant external body (see below).
          </li>
        </ul>
        <ul>
          <li>
            To request a review, email Info@nursingcarebooking.com with the subject line "Complaint
            Review Request" and include your original complaint reference number.
          </li>
        </ul>
      </section>

      <section id="external-bodies">
        <h2>External Bodies</h2>
        <p>If you have exhausted our internal complaints process, you may contact:</p>
        <ul>
          <li>
            <strong>Care Quality Commission (CQC):</strong> For concerns about the quality and
            safety of regulated care services. Website: cqc.org.uk, Telephone: 03000 616161
          </li>
          <li>
            <strong>Information Commissioner's Office (ICO):</strong> For concerns about data
            protection and privacy. Website: ico.org.uk, Telephone: 0303 123 1113
          </li>
          <li>
            <strong>Local Government and Social Care Ombudsman:</strong> For complaints about adult
            social care. Website: lgo.org.uk, Telephone: 0300 061 0614
          </li>
          <li>
            <strong>Citizens Advice:</strong> For general consumer advice and support. Website:
            citizensadvice.org.uk
          </li>
          <li>
            <strong>Action Fraud:</strong> For concerns about fraud. Website:
            actionfraud.police.uk, Telephone: 0300 123 2040
          </li>
        </ul>
      </section>

      <section id="confidentiality">
        <h2>Confidentiality</h2>
        <ul>
          <li>All complaints are handled in strict confidence.</li>
          <li>
            Information is only shared with those who need to know in order to investigate and
            resolve the complaint.
          </li>
          <li>
            We will not share your personal details with the other party without your consent, unless
            required by law or necessary for safeguarding purposes.
          </li>
          <li>
            Raising a complaint will not negatively affect your account or access to our services.
          </li>
          <li>
            We do not tolerate retaliation against anyone who raises a genuine complaint.
          </li>
        </ul>
      </section>

      <section id="feedback">
        <h2>Using Feedback to Improve</h2>
        <p>
          We analyse complaint trends to identify areas for improvement. Feedback from complaints
          contributes to:
        </p>
        <ul>
          <li>Platform feature improvements</li>
          <li>Updates to our policies and procedures</li>
          <li>Enhanced provider vetting processes</li>
          <li>Improved customer support</li>
        </ul>
        <ul>
          <li>
            We are committed to continuous improvement and value the feedback our users provide.
          </li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <ContactBlock
          variant="compact"
          introText="For complaints or questions about this procedure:"
        />
      </section>
    </PolicyLayout>
  );
};
